// Small shared vocabulary for tax documents on the web surfaces: the toast
// line every money write shows, the open-in-new-tab viewer, and the label for
// a document kind. Shared so RecordPaymentDialog, /new-sale, Booking detail and
// the customer record cannot drift into describing the same document
// differently.

import { getDocumentHtml, type InvoiceDocumentType, type PaymentDocumentSummary, type ServiceReceiptOutcome } from './api'

/** The human name of a document kind, for list rows (the full title lives in the snapshot). */
export function docTypeLabel(type: InvoiceDocumentType): string {
  if (type === 'service_receipt') return 'Service receipt'
  return type === 'receipt' ? 'Receipt' : type === 'adjustment_note' ? 'Adjustment note' : 'Invoice'
}

/**
 * What a check-out or a practitioner change did about the service receipt.
 * Only a receipt that went out is news: a block is ordinary, and most sessions
 * get none, so it would be noise on every check-out.
 */
export function serviceReceiptToastLine(outcome: ServiceReceiptOutcome | null | undefined): string | undefined {
  if (!outcome?.issued || !outcome.minted) return undefined
  const name = `Service receipt ${outcome.document_number ?? ''}`.trim()
  if (outcome.sent) return `${name} emailed to ${outcome.recipient} for their health fund / insurance claim.`
  if (outcome.send_error) return `${name} issued, but the email failed: ${outcome.send_error}`
  if (outcome.send_skipped === 'email_unconfigured') return `${name} issued — email is not configured for this business, so it was not sent.`
  return `${name} issued — the client has no email on file, so it was not sent.`
}

/**
 * Open the frozen snapshot in a new tab. Fetched with the JWT, so it cannot
 * just be a link. Returns 'blocked' when the browser refused the popup so the
 * caller can tell the operator; revoking the URL is delayed because revoking
 * immediately races the navigation and shows a blank page.
 */
export async function openDocumentInNewTab(invoiceId: string): Promise<'opened' | 'blocked'> {
  const html = await getDocumentHtml(invoiceId)
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
  const opened = window.open(url, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return opened ? 'opened' : 'blocked'
}

export function documentToastLine(doc: PaymentDocumentSummary | null | undefined): string | undefined {
  if (!doc) return undefined
  const name = `${doc.title ?? 'Document'} ${doc.document_number ?? ''}`.trim()
  if (!doc.issued) {
    return doc.reason === 'setup_required'
      ? 'No tax document was issued — set the ABN and practice address in Settings → Invoices & tax first.'
      : 'The tax document could not be issued; it can still be sent from the booking or sale later.'
  }
  if (doc.sent) return `${name} emailed to ${doc.recipient}.`
  if (doc.send_skipped === 'no_recipient') return `${name} issued — the customer has no email on file, so it was not sent.`
  if (doc.send_skipped === 'email_unconfigured') return `${name} issued — email is not configured for this business, so it was not sent.`
  if (doc.send_error) return `${name} issued, but the email failed: ${doc.send_error}`
  return `${name} issued.`
}
